/**
 * Lazy load scripts.
 *
 * Loads dependencies based on which page gets loaded.
 *
 * @module app/components/PageDependencies
 */
import _ from 'lodash';
import LazyLoadingScript from './LazyLoadingScript';

// jshint maxstatements:false

/**
 * Contains page-to-pageDeps mappings.
 *
 * Note the mapping values are arrays of page dependencies, that will be
 * matched against keys in {dependencies} object.
 *
 * @type {Object}
 *
 * @private
 */
const pageDependencies = window.whStatic.config.pageDependencies;

/**
 * Contains dependency-to-assetLocation mappings.
 *
 * Note the mapping values can either be a url (string), array of urls,
 * or an object, containing 'js' and/or 'css'-keyed array of url(s).
 *
 * @type {Object}
 *
 * @private
 */
const dependencies = window.WH.sportsbook.frontEndConfig.pageDeps;

/**
 * Loads asset(s) defined in given {@param deps} param.
 *
 * @param {Object} deps  dependency object containing 'js'-keyed array of asset
 * locations, and/or 'css'-keyed array of _style_ asset locations.
 *
 * @returns {Array}  array or asset(s) locations.
 *
 * @private
 */
const loadDepAssets = deps => {
    let assets = [];

    if (deps) {
        if (deps.js) {
            assets = deps.js;
        }

        if (deps.css) {
            assets = assets.concat(deps.css.map(LazyLoadingScript.toCssAsset));
        }
    }

    return assets;
};

/**
 * Load all scripts that are required for provided page.
 *
 * @param {String} page Page id e.g "event"
 * @param {Function} callback
 *
 * @returns {void}
 */
const loadPageDependencies = function loadPageDependencies(page, callback) {

    if (!(dependencies && pageDependencies && pageDependencies[page])) {

        callback();
        return;
    }

    /**
     * Get dependency url(s) for provided key
     *
     * @param {String} dependency dependency key
     *
     * @returns {String|Array} either single url, or array of url(s) pointing to the asset(s)
     */
    const getDependency = function getDependency(dependency) {

        const dep = dependencies[dependency];
        if (!dep) {
            throw new Error('Missing dependency definition: "' + dependency + '" for page: "' + page + '"');
        }

        if (typeof dep === 'object') {
            return loadDepAssets(dep);
        }

        return dep;
    };

    const deps = _.flatMap(pageDependencies[page], getDependency);
    if (deps.length) {
        LazyLoadingScript.loadAll(deps, callback);
    } else {
        callback();
    }
};


/**
 * Public functions
 *
 * @type {Object}
 */
const api = {
    loadPageDependencies,
};

export default api;
